/**
 * Inline list of attachments staged on the current composer draft. Renders
 * one chip per file with an upload-in-progress spinner, error pill, or a
 * remove button when the file is ready. Visual style matches the outline
 * action buttons used elsewhere in the workbench so attachments feel like
 * a subtle adjunct to the prompt rather than a competing surface.
 */
import React from 'react';
import { Loader2, X, AlertTriangle, FileText } from 'lucide-react';

function fmtSize(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AttachmentChips({ items, onRemove, disabled = false }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it) => {
        const isUploading = it.status === 'uploading';
        const isError = it.status === 'error';
        return (
          <div
            key={it.id}
            className={
              `group inline-flex max-w-[14rem] items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${
                isError
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : isUploading
                    ? 'border-gray-200 bg-gray-50 text-gray-600'
                    : 'border-gray-200 bg-white text-gray-700'
              }`
            }
            title={isError ? it.error : it.filename}
          >
            {isUploading ? (
              <Loader2 className="h-3 w-3 shrink-0 animate-spin opacity-70" aria-hidden />
            ) : isError ? (
              <AlertTriangle className="h-3 w-3 shrink-0 opacity-80" aria-hidden />
            ) : (
              <FileText className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
            )}
            <span className="truncate">{it.filename}</span>
            {!isUploading && !isError ? (
              <span className="shrink-0 text-[10px] text-gray-400">{fmtSize(it.size)}</span>
            ) : null}
            {isUploading ? (
              <span className="shrink-0 text-[10px] text-gray-400">{`${it.percent || 0}%`}</span>
            ) : null}
            {!disabled ? (
              <button
                type="button"
                onClick={() => onRemove?.(it.id)}
                className="ml-0.5 rounded-full p-0.5 text-gray-400 hover:bg-gray-200/60 hover:text-gray-700"
                aria-label={`Remove ${it.filename}`}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
